import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMMON_KEYS, NS } from '../constants/keys';
import { setAppLocale, SUPPORTED_LOCALES, SupportedLocale } from '../locale';

type LocaleSwitcherProps = {
  /** Navbar: header icon button on green bar. Inline: clean gray border for page content. */
  variant?: 'navbar' | 'inline';
};

type LanguageOption = {
  code: SupportedLocale;
  labelKey?: string;
  nativeName: string;
};

const LANGUAGES: LanguageOption[] = [
  { code: 'en', labelKey: COMMON_KEYS.locale.english, nativeName: 'English' },
  { code: 'id', labelKey: COMMON_KEYS.locale.indonesian, nativeName: 'Bahasa Indonesia' },
  { code: 'si', nativeName: 'සිංහල' },
  { code: 'ta', nativeName: 'தமிழ்' },
  { code: 'ru', nativeName: 'Русский' },
];

export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ variant = 'inline' }) => {
  const { t, i18n } = useTranslation(NS.common);
  const current = (SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
    ? i18n.language
    : 'en') as SupportedLocale;
  const isNavbar = variant === 'navbar';
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (locale: SupportedLocale) => {
    setAppLocale(locale);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className='relative inline-block text-left shrink-0'>
      { /* Translate "文A" Header Icon Button */ }
      <button
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label={t(COMMON_KEYS.locale.switchLabel)}
          className={`flex items-center justify-center p-2 rounded-xl transition-all duration-200 ${
            isNavbar
              ? 'text-[var(--navbar-text,#ffffff)] hover:bg-[var(--navbar-btn-hover,rgba(255,255,255,0.2))] active:scale-95'
              : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 shadow-2xs active:scale-95'
          }`}
          title='Change Language'
      >
        { /* "文A" Google / Global Translate Icon */ }
        <svg
            className='w-5 h-5'
            viewBox='0 0 24 24'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
        >
          <path d='M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z' />
        </svg>
      </button>

      { /* Popover Dropdown Menu (Exact match to reference UI) */ }
      { isOpen && (
        <div className='absolute right-0 mt-2 w-44 rounded-2xl bg-white shadow-2xl border border-slate-100 py-2 z-50 transform origin-top-right transition-all duration-150 animate-in fade-in slide-in-from-top-1'>
          { LANGUAGES.map((lang) => {
            const isSelected = current === lang.code;
            const labelText = lang.labelKey ? t(lang.labelKey) : lang.nativeName;

            return (
              <button
                  key={lang.code}
                  type='button'
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-slate-100/80 text-slate-900 font-semibold'
                      : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
              >
                { /* Checkmark column */ }
                <div className='w-4 flex items-center justify-center shrink-0'>
                  { isSelected && (
                    <svg className='w-4 h-4 text-slate-700' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                      <polyline points='20 6 9 17 4 12' />
                    </svg>
                  ) }
                </div>

                { /* Native Language Name */ }
                <span className='truncate'>{ labelText || lang.nativeName }</span>
              </button>
            );
          }) }
        </div>
      ) }
    </div>
  );
};

export default LocaleSwitcher;
